import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Flame, Image, Info, MoreHorizontal, Plus, RectangleGoggles, RotateCcw, Star, Tag, Trash, Upload, Video } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { useParams } from 'react-router-dom';
import type { Listings } from '@/types/PropertyListings';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { motion } from 'framer-motion';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
  } from "@/components/ui/popover"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
  } from "@/components/ui/dialog"

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from '@/components/ui/button';
import type { ListingMedias, PendingMedias, PropertyListingMedias } from '@/types/PropertyListingMedias';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { Badge } from '@/components/ui/badge';

interface _GalleryDetails {
    listing?: Listings | null;
    setListing?: React.Dispatch<React.SetStateAction<Listings | null>>;
    markAsDuplicate?: boolean;
    edit?: boolean;
}

export interface GalleryDetailsRef {
    callback: () => boolean;
}

interface _SortableMedia {
    media: ListingMedias | PendingMedias;
    removeMedia: (id: string) => Promise<void>;
    manageMediaLabel: (id: string, label: string) => Promise<void>;
    updateMediaCover: (id: string) => void;
    isPendingMedia: (media: ListingMedias | PendingMedias) => media is PendingMedias;
    retryUpload: (media: PendingMedias) => void;
}

const SortableMedia : React.FC<_SortableMedia> = ({ 
    media,
    removeMedia,
    manageMediaLabel,
    updateMediaCover,
    isPendingMedia,
    retryUpload
}) => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [popOverOpen, setPopOverOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const { 
        attributes, 
        listeners, 
        setNodeRef, 
        transform, 
        transition 
    } = useSortable({ id: media.id });
    const [selectedConstant, setSelectedConstant] = useState<string | null>(null);
    const labelInputRef = useRef<HTMLInputElement>(null);
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleBadgeClick = (label: string, constant: string) => {
        if (selectedConstant === constant) {
            setSelectedConstant(null);
            if (labelInputRef.current) {
                labelInputRef.current.value = '';
            }
        } else {
            setSelectedConstant(constant);
            if (labelInputRef.current) {
                labelInputRef.current.value = label;
            }
        }
    };


    const handleMediaLabel = async () => {
        const value = labelInputRef.current?.value?.trim();
    
        try {
            setLoading(true);
    
            if (value) {
                await manageMediaLabel(String(media.id), value);
            }
    
            setDialogOpen(false);
            setPopOverOpen(false);
            setSelectedConstant(null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
  
    return (
        <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="border h-[100px] w-[100px] rounded cursor-grab active:cursor-grabbing relative">
            {
                media.mediaType === 3 ? (
                    <video
                    {...listeners}
                    className="h-full w-full object-cover"
                    src={"file" in media ? media.url : `${import.meta.env.VITE_AWS_S3}/${media.path}`}
                    autoPlay
                    loop
                    muted
                    playsInline />
                ) : (
                    <div
                    {...listeners}
                    className="h-full w-full rounded bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${ "file" in media ? media.url : `${import.meta.env.VITE_AWS_S3}/${media.path}` })`,
                    }}/>
                )
            }
            {
                'uploading' in media && media.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
                        <Spinner className="w-6 h-6 text-white" />
                    </div>
                )
            }
            {
                'uploading' in media && !media.uploading && media.error && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/30 rounded gap-2">
                        <RotateCcw className="w-6 h-6 text-white" onClick={() => retryUpload(media)}/>
                    </div>
                )
            }
            {
                !('uploading' in media) || (!media.uploading && !media.error) ? (
                    <>
                        {
                            media.mediaType === 1 ? (
                                <div className="absolute top-1 right-1 flex gap-1">
                                    <Popover open={popOverOpen} onOpenChange={setPopOverOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                            size="icon"
                                            variant="outline"
                                            className="h-6 w-6"
                                            >
                                            <MoreHorizontal />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                        side="right"
                                        sideOffset={8}
                                        className="w-40 p-2 text-sm">
                                            <div className="flex flex-col gap-1">
                                            <Button
                                            variant="outline"
                                            size="sm"

                                            disabled={!isPendingMedia(media) && media.isCover === 1}
                                            onClick={() => {
                                                if (!isPendingMedia(media) && media.isCover === 0) {
                                                    updateMediaCover(String(media.id));
                                                    setPopOverOpen(false);
                                                }
                                            }}>
                                                <Image />
                                                <span>Cover</span>
                                            </Button>

                                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                                <DialogTrigger asChild>
                                                    <Button size="sm" variant="outline">
                                                        <Tag />
                                                        <span>Label</span>
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="sm:max-w-[300px]">
                                                    <DialogHeader>
                                                        <DialogTitle>Label room</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-2">
                                                        <Input placeholder="Enter tag..." ref={labelInputRef} disabled={selectedConstant ? true : false}/>
                                                        <div className='flex gap-1 flex-wrap'>
                                                            <Badge 
                                                            variant={selectedConstant === 'zoning' ? `default` : `outline`} 
                                                            className='cursor-pointer'
                                                            onClick={() => {
                                                                setSelectedConstant('zoning');
                                                                handleBadgeClick('Zoning', 'zoning');
                                                            }}>Zoning</Badge>
                                                            <Badge 
                                                            className='cursor-pointer'
                                                            variant={selectedConstant === 'floor_plan' ? `default` : `outline`} 
                                                            onClick={() => {
                                                                setSelectedConstant('floor_plan');
                                                                handleBadgeClick('Floor plan', 'floor_plan');
                                                            }}>Floor plan</Badge>
                                                            <Badge 
                                                            className='cursor-pointer'
                                                            variant={selectedConstant === 'location' ? `default` : `outline`} 
                                                            onClick={() => {
                                                                setSelectedConstant('location');
                                                                handleBadgeClick('Location', 'location');
                                                            }}>Location</Badge>
                                                              <Badge 
                                                            className='cursor-pointer'
                                                            variant={selectedConstant === 'location' ? `default` : `outline`} 
                                                            onClick={() => {
                                                                setSelectedConstant('measurement');
                                                                handleBadgeClick('Measurement', 'measurement');
                                                            }}>Measurement</Badge>
                                                        </div>
                                                        <Button
                                                        size="sm"
                                                        className="float-end mt-3"
                                                        onClick={handleMediaLabel}
                                                        >
                                                        {loading ? (
                                                            <>
                                                            Saving
                                                            <Spinner size="sm" />
                                                            </>
                                                        ) : (
                                                            "Save"
                                                        )}
                                                        </Button>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                            <Button

                                                variant="destructive"
                                                size="sm"
                                                onClick={async () => {
                                                    setDeleting(true);
                                                    await removeMedia(media);
                                                    setDeleting(false);
                                                }}>
                                                <Trash />
                                                {
                                                    !deleting ? 'Delete' : <Spinner />
                                                }
                                            </Button>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            ) : (
                                <div className="absolute top-1 right-1">
                                    <Button
                                    type='button'
                                    variant={"destructive"}
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => removeMedia(media)}>
                                        <Trash />
                                    </Button>
                                </div>
                            )
                        }
                        {
                            !isPendingMedia(media) && media.mediaType === 1 && media.isCover === 1 && (
                                <Button
                                    type="button"
                                    className="absolute top-1 left-1 h-6 w-6 bg-white">
                                    <Star color="black" />
                                </Button>
                            )
                        }

                        {
                            !isPendingMedia(media) && media.mediaType === 1 && media.label && (
                                <div className="absolute bottom-1 right-1 bg-black/60 rounded px-2 py-1 text-xs text-white">
                                    <span>{media.label}</span>
                                </div>
                            )
                        }
                    </>
                ) : ('uploading' in media && media.error) ? (
                    <div className="absolute top-1 right-1">
                        <Button
                        variant="destructive"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                            e.preventDefault();
                            removeMedia(media)
                        }}>
                            <Trash />
                        </Button>
                    </div>
                ) : null
            }
        </div>
    );
};

/** Agent intro video for this listing. Kept in step with the API's mediaType 5. */
const AGENT_VIDEO_MEDIA_TYPE = 5;
const MAX_AGENT_VIDEO_SECONDS = 30;

const readVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
        const element = document.createElement('video');
        element.preload = 'metadata';
        element.onloadedmetadata = () => {
            URL.revokeObjectURL(element.src);
            resolve(element.duration);
        };
        element.onerror = () => {
            URL.revokeObjectURL(element.src);
            reject(new Error('Could not read that video file.'));
        };
        element.src = URL.createObjectURL(file);
    });
};

const GalleryDetails = forwardRef<GalleryDetailsRef, _GalleryDetails>(({
    listing, 
    setListing,
    markAsDuplicate
}, ref) => {
    const { listingId } = useParams();
    const [dragOver, setDragOver] = useState<boolean>(false);
    const imagesInputRef = useRef<HTMLInputElement>(null);
    const floorPlanInputRef = useRef<HTMLInputElement>(null);
    const videosInputRef = useRef<HTMLInputElement>(null);
    const vrtourInputRef = useRef<HTMLInputElement>(null);
    const agentVideoInputRef = useRef<HTMLInputElement>(null);
    const sensors = useSensors(useSensor(PointerSensor));

    const agentVideo = listing?.medias.find(
        (x: PropertyListingMedias | PendingMedias) => x.mediaType === AGENT_VIDEO_MEDIA_TYPE
    ) ?? null;

    // The intro video is the listing owner's own introduction. Co-agency and
    // co-share agents can still work the listing, but not front it — the API
    // enforces the same rule on upload and delete.
    const currentAgent = useSelector((state: RootState) => state.auth.agent);
    const isListingOwner = !!listing && listing.createdBy === currentAgent?.id;

    const handleDragEnd = async (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        if (!listing || !setListing) return;

        const oldIndex = listing.medias.findIndex((i) => i.id === active.id);
        const newIndex = listing.medias.findIndex((i) => i.id === over.id);
        const reordered = arrayMove(listing.medias, oldIndex, newIndex);

        setListing({ 
            ...listing, 
            medias: reordered 
        });

        try {
            var formdata = new FormData();
            reordered.forEach((m) => formdata.append('ids', String(m.id)));

            const response = await api.post(`/listings/${listingId}/medias/reorder`, formdata);

            if(!response.data.status) {
                throw new Error(response.data.error);
            }

        } catch (err: any) {
            toast.error('Failed to update media order');
        }
    };

    const callback = () => {
        if (!listing || listing.medias.length < 5) {
            toast.error('A minimum of 5 images are required to proceed');
            return false;
        }
        return true;
    };

    const removeMedia = async (media: PropertyListingMedias) => {
        try {
            if (!listing || !setListing) return;
            if(media.listingId) {
                const response = await api.delete(`/listings/${listingId}/medias/${media.id}`);
                if(!response.data.status) {
                    throw new Error(response.data.error);
                }
            }
            setListing((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    medias: prev.medias.filter((m) => String(m.id) !== String(media.id)),
                };
            });
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const manageMediaLabel = async (mediaId: string, label: string, constant = null) => {
        try {
            if (!listing || !setListing) return;

            var formdata = new FormData()
            formdata.append('label', label);
            if(constant) {
                formdata.append('constant', constant);
            }

            const response = await api.post(`/listings/${listingId}/medias/${mediaId}/label`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const updatedMedia = response.data.media;

            setListing((prev) =>
                prev
                ? {
                    ...prev,
                    medias: prev.medias.map((m) =>
                        m.id === Number(mediaId) ? updatedMedia : m
                    ),
                }
                : prev
            );
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const updateMediaCover = async (mediaId: string) => {
        try {
            if (!listing || !setListing) return;

            const media = listing.medias.find(x => x.id === Number(mediaId));
            if(!media) throw new Error('Something went wrong');

            const response = await api.post(`/listings/${listingId}/medias/${mediaId}/cover`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setListing((prev) =>
                prev
                  ? {
                      ...prev,
                      medias: prev.medias.map((m) =>
                        m.id === Number(mediaId) ? response.data.media : m
                      ),
                    }
                : prev
            );
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const retryUpload = (pending: PendingMedias) => {
        if (!listing || !setListing) return;
        setListing(prev =>
          prev
            ? {
                ...prev,
                medias: prev.medias.map(m =>
                  m.id === pending.id ? { ...pending, uploading: true } : m
                ),
              }
            : prev
        );
        processFilesAndUpload([pending.file], String(pending.mediaType), pending.id);
    };

    const processFilesAndUploadV2 = async (
        files: File[],
        type?: string,
        pendingId?: string
    ) => {
        if (!listing || !setListing) return;
        if (!files.length || !type) return;
        const mediaType = Number(type);
        const allowedFiles = files.filter(file => {
            const fileExt = file.name.split('.').pop()?.toLowerCase();
            if (mediaType === 1 || mediaType === 2) {
                return fileExt === 'jpg' || fileExt === 'jpeg' || fileExt === 'png';
            } else if (mediaType === 3) {
                return fileExt === 'mp4';
            }
            return false;
        });
    
        if (!allowedFiles.length) {
            throw new Error(mediaType === 3 ? 'Only MP4 type is allowed.' : 'Only JPG, JPEG and PNG type is allowed.');
        }
      
        if (!pendingId) {
            const tempFiles: PendingMedias[] = files.map(file => ({
                id: `temp-${Date.now()}-${file.name}`,
                url: URL.createObjectURL(file),
                uploading: true,
                mediaType: Number(type),
                file,
                error: false,
            }));
      
            setListing(prev =>
                prev ? { ...prev, medias: [...prev.medias, ...tempFiles] } : prev
            );
        }
       
        try {
            const formData = new FormData();
            formData.append('type', String(type));
            files.forEach(file => formData.append("files", file));
        
            const response = await api.post(
                `/listings/${listingId}/medias`,
                formData,
                { 
                    headers: { 
                        'Content-Type': 'multipart/form-data' 
                    } 
                }
            );
            if (!response.data.status) throw new Error(response.data.error || 'Upload failed');
            console.log(response.data)
            setListing(prev =>
                prev
                  ? {
                      ...prev,
                      medias: prev.medias.map(m => {
                        if (pendingId && m.id === pendingId) {
                          const uploaded = response.data.files.shift();
                          if (uploaded) return { ...uploaded };
                        } 
                        else if (!pendingId && m.uploading === true && "file" in m) {
                          const uploaded = response.data.files.shift();
                          if (uploaded) return { ...uploaded };
                        }
                        return m;
                      }),
                    }
                  : prev
              );
              
        } catch (err) {
            console.error('Upload error:', err);
        
            setListing(prev =>
                prev
                ? {
                    ...prev,
                    medias: prev.medias.map(m =>
                        pendingId
                        ? m.id === pendingId
                            ? { ...m, uploading: false, error: true }
                            : m
                        : "file" in m
                        ? { ...m, uploading: false, error: true }
                        : m
                    ),
                    }
                : prev
            );
        }
    };

    const processFilesAndUpload = async (
        files: File[],
        type?: string,
        pendingId?: string
    ) => {
        if (!listing || !setListing) return;
        if (!files.length || !type) return;
        const mediaType = Number(type);
        const isVideoType = mediaType === 3 || mediaType === AGENT_VIDEO_MEDIA_TYPE;
        const allowedFiles = files.filter(file => {
            const fileExt = file.name.split('.').pop()?.toLowerCase();
            if (mediaType === 1 || mediaType === 2) {
                return fileExt === 'jpg' || fileExt === 'jpeg' || fileExt === 'png';
            } else if (isVideoType) {
                return fileExt === 'mp4';
            }
            return false;
        });

        if (!allowedFiles.length) {
            throw new Error(isVideoType ? 'Only MP4 type is allowed.' : 'Only JPG, JPEG and PNG type is allowed.');
        }

        // The agent intro clip is capped much shorter than a property walkthrough,
        // so it is checked before paying for the upload. The API enforces it too.
        if (mediaType === AGENT_VIDEO_MEDIA_TYPE) {
            const duration = await readVideoDuration(allowedFiles[0]);
            if (duration > MAX_AGENT_VIDEO_SECONDS) {
                throw new Error(`Video is ${Math.round(duration)}s long. Maximum is ${MAX_AGENT_VIDEO_SECONDS}s.`);
            }
        }
      
        if (!pendingId) {
            const tempFiles: PendingMedias[] = files.map(file => ({
                id: `temp-${Date.now()}-${file.name}`,
                url: URL.createObjectURL(file),
                uploading: true,
                mediaType: Number(type),
                file,
                error: false,
            }));
      
            setListing(prev =>
                prev ? { ...prev, medias: [...prev.medias, ...tempFiles] } : prev
            );
        }
       
        try {
            const formData = new FormData();
            formData.append('type', String(type));
            files.forEach(file => formData.append("files", file));
        
            const response = await api.post(
                `/listings/${listingId}/medias`,
                formData,
                { 
                    headers: { 
                        'Content-Type': 'multipart/form-data' 
                    } 
                }
            );
            if (!response.data.status) throw new Error(response.data.error || 'Upload failed');
            
            
            // Handle rejected files - show toast errors
            if (response.data.rejectedFiles && response.data.rejectedFiles.length > 0) {
                response.data.rejectedFiles.forEach((rejected: any) => {
                    let errorMessage = `${rejected.filename}: ${rejected.reason}`;
                    
                    // Add specific labels if available
                    if (rejected.labels && rejected.labels.length > 0) {
                        errorMessage += ` (${rejected.labels.join(', ')})`;
                    }
                    
                    toast.error(errorMessage, {
                        duration: 5000,
                    });
                });
            }
            
            // More robust filename matching that handles encoding issues
            const normalizeForComparison = (filename: string) => {
                // Remove file extension for comparison
                const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.')) || filename;
                // Remove emojis, special chars, and normalize whitespace
                return nameWithoutExt
                    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
                    .replace(/[^\w\s.-]/g, '') // Remove special characters
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim()
                    .toLowerCase();
            };
            
            const isRejectedFile = (originalFilename: string) => {
                if (!response.data.rejectedFiles) return false;
                
                const normalizedOriginal = normalizeForComparison(originalFilename);
                
                return response.data.rejectedFiles.some((rejected: any) => {
                    // Try exact match first
                    if (rejected.filename === originalFilename) return true;
                    
                    // Try normalized comparison (handles encoding issues)
                    const normalizedRejected = normalizeForComparison(rejected.filename);
                    
                    // Match if normalized versions are the same
                    if (normalizedOriginal === normalizedRejected) return true;
                    
                    // Fuzzy match: check if one contains the other (for partial matches)
                    if (normalizedOriginal.length > 10 && normalizedRejected.length > 10) {
                        return normalizedOriginal.includes(normalizedRejected) || 
                               normalizedRejected.includes(normalizedOriginal);
                    }
                    
                    return false;
                });
            };
            
            setListing(prev => {
                if (!prev) return prev;
                
                return {
                    ...prev,
                    medias: prev.medias.map(m => {
                        // Handle updating specific pending upload
                        if (pendingId && m.id === pendingId) {
                            const uploaded = response.data.files.shift();
                            if (uploaded) return { ...uploaded };
                            
                            // Check if this specific file was rejected
                            if ('file' in m && isRejectedFile(m.file.name)) {
                                return { 
                                    ...m, 
                                    uploading: false, 
                                    error: true
                                };
                            }
                        } else if (!pendingId && m.uploading === true && "file" in m) {
                            if (isRejectedFile(m.file.name)) {
                                return { 
                                    ...m, 
                                    uploading: false, 
                                    error: true
                                };
                            }
                            
                            const uploaded = response.data.files.shift();
                            if (uploaded) return { ...uploaded };
                        }
                        return m;
                    })
                };
            });
        } catch (err: any) {
            console.error('Upload error:', err);
            toast.error(err.response?.data?.error || err.message || 'Upload failed');
        
            setListing(prev =>
                prev
                ? {
                    ...prev,
                    medias: prev.medias.map(m =>
                        pendingId
                        ? m.id === pendingId
                            ? { ...m, uploading: false, error: true }
                            : m
                        : "file" in m
                        ? { ...m, uploading: false, error: true }
                        : m
                    ),
                    }
                : prev
            );
        }
    };

    const uploadMedia = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!e.target.files || e.target.files.length === 0) return;
            const type = e.currentTarget.dataset.type;
            const files = Array.from(e.target.files);
            
            await processFilesAndUpload(files, type);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } finally {
            e.target.value = '';
        }
    };
      

    const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault(); 
        const type = e.currentTarget.dataset.type;
        try {
            setDragOver(false);
            if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
            const files = Array.from(e.dataTransfer.files);
            
            await processFilesAndUpload(files, type);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    };

    const isPendingMedia = (m: ListingMedias | PendingMedias): m is PendingMedias => {
        return 'uploading' in m;
    };

    useImperativeHandle(ref, () => ({
        callback,
    }));
  
    return (
        <section
        onDragOver={(e) => {
            e.preventDefault(); 
            setDragOver(true);
        }}
        onDragLeave={() => {
            setDragOver(false); 
        }}>
            <div className="mt-5">
                <div className="mb-5">
                    <div className='flex justify-between'>
                        <div>
                            <div className="flex items-center gap-1">
                                <Image />
                                <Label>Upload your images</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Info size={16} className="cursor-pointer text-black" />
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 text-sm">
                                        Drag the image to the first as your cover photo.
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="flex items-center gap-1 text-red-400">
                                <Info size={14} />
                                <span className="text-sm">A minimum of 5 images are required to proceed</span>
                            </div>
                        </div>
                        <Button 
                        type='button' 
                        size={'sm'} 

                        onClick={() => {
                            imagesInputRef.current?.click();
                        }}>
                            <Plus/>
                        </Button>
                    </div>
                    <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}>
                        <Card className={`mt-3 ${listing?.medias.length === 0 ? 'cursor-pointer' : ''}`}>
                            {
                                dragOver ? 
                                    <CardContent 
                                    data-type={1}
                                    onDrop={handleFileDrop}>
                                        <div className="flex flex-col justify-center items-center min-h-[100px] p-4 border border-dashed rounded-lg">
                                            <motion.div
                                                className="mb-3 text-black"
                                                animate={{ y: [0, -10, 0] }}
                                                transition={{
                                                    duration: 1.2,
                                                    ease: "easeInOut",
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                    delay: 0.2 
                                                }}
                                            >
                                                <Flame color='orange'/>
                                            </motion.div>
                                            <p className="text-center font-medium text-gray-700">
                                                Drop your files here
                                            </p>
                                            <p className="text-center text-sm text-black mt-1">
                                                (or click to select)
                                            </p>
                                        </div>
                                    </CardContent>
                                :
                                    <CardContent
                                    onClick={() => {
                                        if(listing?.medias.filter(x => x.mediaType === 1).length === 0) {
                                            imagesInputRef.current?.click();
                                        }
                                    }}>
                                    {
                                        listing && listing.medias.filter(x => x.mediaType === 1).length > 0 ? (
                                                <SortableContext
                                                items={listing.medias.filter(x => x.mediaType === 1).map((m) => m.id)}
                                                strategy={rectSortingStrategy}>
                                                    <div className="flex flex-wrap gap-1">
                                                    {
                                                        listing.medias.filter(x => x.mediaType === 1).map((media) => (
                                                            <SortableMedia 
                                                            key={media.id} 
                                                            media={media} 
                                                            removeMedia={removeMedia}
                                                            manageMediaLabel={manageMediaLabel}
                                                            updateMediaCover={updateMediaCover}
                                                            isPendingMedia={isPendingMedia}
                                                            retryUpload={retryUpload}/>
                                                        ))
                                                    }
                                                    </div>
                                                </SortableContext>
                                        ) : (
                                            <div className="flex flex-col justify-center items-center min-h-[100px]">
                                                <Upload className="mb-3" />
                                                <Label className="text-center">Drop here like it's hot</Label>
                                            </div>
                                        )
                                    }
                                    </CardContent>
                            }
                        </Card>
                    </DndContext>
                    <Input
                    type="file"
                    ref={imagesInputRef}
                    multiple
                    hidden
                    accept=".jpg,.jpeg,.png"
                    data-type={1}
                    onChange={uploadMedia} />
                </div>
                {/* 
                <div className="mb-5">
                    <div className='flex justify-between'>
                        <div className="flex items-center gap-1">
                            <Icon iconNode={floorPlan} />
                            <Label>Upload floor plan</Label>
                        </div>
                        <Button 
                        type='button'
                        size={'sm'} 
                        className='cursor-pointer'
                        onClick={() => {
                            floorPlanInputRef.current?.click();
                        }}>
                            <Plus />
                        </Button>
                    </div>
                    
                    <Card className="mt-3 cursor-pointer">
                        {
                            dragOver ? 
                                <CardContent 
                                data-type={2}
                                onDrop={handleFileDrop}>
                                    <div className="flex flex-col justify-center items-center min-h-[100px] p-4 border border-dashed rounded-lg">
                                        <motion.div
                                            className="mb-3 text-black"
                                            animate={{ y: [0, -10, 0] }}
                                            transition={{
                                                duration: 1.2,
                                                ease: "easeInOut",
                                                repeat: Infinity,
                                                repeatType: "loop",
                                                delay: 0.2 
                                            }}
                                        >
                                            <Flame color='orange'/>
                                        </motion.div>
                                        <p className="text-center font-medium text-gray-700">
                                            Drop your files here
                                        </p>
                                        <p className="text-center text-sm text-black mt-1">
                                            (or click to select)
                                        </p>
                                    </div>
                                </CardContent>
                            :
                                <CardContent onClick={() => {
                                    if(listing?.medias.filter(x => x.mediaType === 2).length === 0) {
                                        floorPlanInputRef.current?.click();
                                    }
                                }}>
                                    {
                                        listing && listing.medias.filter(x => x.mediaType === 2).length > 0 ? (
                                            <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}>
                                                <SortableContext
                                                items={listing.medias.filter(x => x.mediaType === 2).map((m) => m.id)}
                                                strategy={rectSortingStrategy}>
                                                    <div className="flex flex-wrap gap-1">
                                                    {
                                                        listing.medias.filter(x => x.mediaType === 2).map((media) => (
                                                            <SortableMedia 
                                                            key={media.id} 
                                                            media={media} 
                                                            removeMedia={removeMedia}
                                                            manageMediaLabel={manageMediaLabel}
                                                            updateMediaCover={updateMediaCover}
                                                            isPendingMedia={isPendingMedia}
                                                            retryUpload={retryUpload}/>
                                                        ))
                                                    }
                                                    </div>
                                                </SortableContext>
                                            </DndContext>
                                        ) : (
                                            <div className="flex flex-col justify-center items-center min-h-[100px]">
                                                <Upload className="mb-3" />
                                                <Label className="text-center">Drop here like it's hot</Label>
                                            </div>
                                        )
                                    }
                                </CardContent>
                        }
                        <Input
                        type="file"
                        ref={floorPlanInputRef}
                        multiple
                        hidden
                        data-type={2}
                        accept=".jpg,.jpeg,.png"
                        onChange={uploadMedia}/>
                    </Card>
                </div> */}

                <div className="mb-5">
                    <div className='flex justify-between'>
                        <div className="flex items-center gap-1">
                            <Video />
                            <Label>Upload short videos</Label>
                        </div>
                        <Button 
                        type='button'
                        size={'sm'} 

                        onClick={() => {
                            videosInputRef.current?.click();
                        }}>
                            <Plus/>
                        </Button>
                    </div>
                   
                    <Card className="mt-3 cursor-pointer">
                        {
                            dragOver ? 
                                <CardContent 
                                data-type={3}
                                onDrop={handleFileDrop}>
                                    <div className="flex flex-col justify-center items-center min-h-[100px] p-4 border border-dashed rounded-lg">
                                        <motion.div
                                            className="mb-3 text-black"
                                            animate={{ y: [0, -10, 0] }}
                                            transition={{
                                                duration: 1.2,
                                                ease: "easeInOut",
                                                repeat: Infinity,
                                                repeatType: "loop",
                                                delay: 0.2 
                                            }}
                                        >
                                            <Flame color='orange'/>
                                        </motion.div>
                                        <p className="text-center font-medium text-gray-700">
                                            Drop your files here
                                        </p>
                                        <p className="text-center text-sm text-black mt-1">
                                            (or click to select)
                                        </p>
                                    </div>
                                </CardContent>
                            :
                                <CardContent onClick={() => {
                                    if(listing?.medias.filter(x => x.mediaType === 3).length === 0) {
                                        videosInputRef.current?.click();
                                    }
                                }}>
                                    {
                                        listing && listing.medias.filter(x => x.mediaType === 3).length > 0 ? (
                                            <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}>
                                                <SortableContext
                                                items={listing.medias.filter(x => x.mediaType === 3).map((m) => m.id)}
                                                strategy={rectSortingStrategy}>
                                                    <div className="flex flex-wrap gap-1">
                                                    {
                                                        listing.medias.filter(x => x.mediaType === 3).map((media) => (
                                                            <SortableMedia 
                                                            key={media.id} 
                                                            media={media} 
                                                            removeMedia={removeMedia}
                                                            manageMediaLabel={manageMediaLabel}
                                                            updateMediaCover={updateMediaCover}
                                                            isPendingMedia={isPendingMedia}
                                                            retryUpload={retryUpload}/>
                                                        ))
                                                    }
                                                    </div>
                                                </SortableContext>
                                            </DndContext>
                                        ) : (
                                            <div className="flex flex-col justify-center items-center min-h-[100px]">
                                                <Upload className="mb-3" />
                                                <Label className="text-center">Drop here like it's hot</Label>
                                            </div>
                                        )
                                    }
                                </CardContent>
                        }
                        <Input
                        type="file"
                        ref={videosInputRef}
                        multiple
                        hidden
                        data-type={3}
                        accept="video/mp4"
                        onChange={uploadMedia}
                        />
                    </Card>
                </div>

                <div className="mb-5">
                    <div className='flex justify-between'>
                        <div className="flex items-center gap-1">
                            <Video />
                            <Label>Agent intro video for this listing</Label>
                        </div>
                        {
                            !agentVideo && isListingOwner && (
                                <Button
                                type='button'
                                size={'sm'}

                                onClick={() => {
                                    agentVideoInputRef.current?.click();
                                }}>
                                    <Plus/>
                                </Button>
                            )
                        }
                    </div>
                    <p className='text-xs text-muted-foreground mt-1'>
                        Optional MP4 up to {MAX_AGENT_VIDEO_SECONDS} seconds, shown in the agent section of this
                        listing. It replaces the intro video on your profile for this listing only.
                    </p>

                    <Card className="mt-3">
                        <CardContent>
                            {
                                agentVideo ? (
                                    <div className='flex flex-wrap items-start gap-3'>
                                        {/* Keyed so replacing the clip remounts the player: a
                                            <video> holds on to the resource it already loaded
                                            when src changes. */}
                                        <video
                                        key={agentVideo.id}
                                        src={isPendingMedia(agentVideo)
                                            ? agentVideo.url
                                            : `${import.meta.env.VITE_AWS_S3}/${agentVideo.path}`}
                                        className='w-[260px] h-[150px] object-contain bg-black rounded-lg'
                                        controls
                                        playsInline
                                        preload='metadata'/>
                                        {isListingOwner && (
                                        <Button
                                        type='button'
                                        variant={'ghost'}
                                        size={'sm'}

                                        disabled={isPendingMedia(agentVideo)}
                                        onClick={() => {
                                            // removeMedia needs the media itself — it reads
                                            // listingId off it to decide whether to call the API.
                                            if (!isPendingMedia(agentVideo)) removeMedia(agentVideo);
                                        }}>
                                            <Trash color='red'/>
                                        </Button>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                    className={`flex flex-col justify-center items-center min-h-[100px] ${isListingOwner ? 'cursor-pointer' : ''}`}
                                    onClick={() => { if (isListingOwner) agentVideoInputRef.current?.click(); }}>
                                        <Upload className="mb-3" />
                                        <Label className={`text-center ${isListingOwner ? 'cursor-pointer' : ''}`}>
                                            {isListingOwner
                                                ? 'Add a short intro for this listing'
                                                : 'Only the listing owner can add an intro video'}
                                        </Label>
                                    </div>
                                )
                            }
                        </CardContent>
                        <Input
                        type="file"
                        ref={agentVideoInputRef}
                        hidden
                        data-type={AGENT_VIDEO_MEDIA_TYPE}
                        accept="video/mp4"
                        onChange={uploadMedia}
                        />
                    </Card>
                </div>

                <div className="mb-5">
                    <div className="flex items-center gap-1">
                        <RectangleGoggles />
                        <Label>Upload virtual tours</Label>
                    </div>
                    <Card className="mt-3 cursor-pointer">
                        <CardContent>
                            <div className="flex flex-col justify-center items-center min-h-[100px]">
                                Coming soon.
                            </div>
                        </CardContent>
                        <Input
                        type="file"
                        ref={vrtourInputRef}
                        multiple
                        data-type={4}
                        hidden
                        onChange={uploadMedia}
                        />
                    </Card>
                </div>
            </div>
        </section>
    );
});
  
export default GalleryDetails;